/**
 * GxWhy (screens §1.5): "Why?" on a KPI tile opens the top three causes since the previous released round, from
 * `RoundReport.attribution.byCause[].kpiDeltas`, each cause's effect on that KPI in its own unit (rates as "+6bp"). Deterministic, no AI;
 * "Explain this number" hands the question to the Coach.
 */
import { useEffect, useId, useRef, useState } from "react";
import type { ContentCatalog, RoundReport } from "@bsim/contracts";
import type { KpiId } from "@bsim/domain";
import { useCoach } from "../coach/CoachDrawer";
import { decisionTitle } from "../decisions/decisionValues";
import { formatKpiChange } from "../format";
import { kpiLabel } from "../format/labels";
import { GxPopover, useSheetMode } from "../theme/Popover";
import { DECISIONS_LABEL, decompose, marketDriversNote, type HeadlineId } from "../results/decomposition";

type Attribution = RoundReport["attribution"];

export interface WhyRow { readonly label: string; readonly text: string; readonly delta: number; /** What the market did, under the market's own row. */ readonly note?: string }

export function causeLabel(cause: Attribution["byCause"][number]["cause"], catalog: ContentCatalog): string {
  switch (cause.kind) {
    case "decision": return decisionTitle(catalog.decisions, cause.decisionId);
    case "event": return "An event this year";
    case "env": return "Rates and the market";
    case "market": return cause.note ?? "Competitor moves";
    case "contractual": return "Existing contracts";
    case "supervisory": return "Supervisory action";
    case "autopilot": return "Standing policy"; // the status-quo twin's plan; never the word "autopilot" in copy
  }
}


/** The three causes that moved this KPI most (either sign); empty when the attribution has none for it. */
export function whyRows(kpi: KpiId, attribution: Attribution, catalog: ContentCatalog): WhyRow[] {
  return attribution.byCause
    .flatMap((c) => { const delta = c.kpiDeltas[kpi] ?? 0; return delta === 0 ? [] : [{ label: causeLabel(c.cause, catalog), delta }]; })
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).slice(0, 3)
    .map((r) => ({ label: r.label, text: formatKpiChange(kpi, r.delta), delta: r.delta }));
}

/** The rows as one plain sentence, for the Coach. */
function answerOf(kpi: KpiId, rows: readonly WhyRow[], fyLabel: string): string | null {
  const [first, ...rest] = rows;
  if (first === undefined) return null;
  return `${kpiLabel(kpi)} in ${fyLabel} moved mainly because of ${first.label} (${first.text})${rest.length === 0 ? "" : `, then ${rest.map((r) => `${r.label} (${r.text})`).join(" and ")}`}. These are first-order effects from your ${fyLabel} attribution, so they need not add up to the whole move.`;
}

/** The same causes as one plain answer, for the Coach ("Explain this number"); null when the attribution has none for the measure. */
export const whyAnswer = (kpi: KpiId, attribution: Attribution, catalog: ContentCatalog, fyLabel: string): string | null => answerOf(kpi, whyRows(kpi, attribution, catalog), fyLabel);

/**
 * The rows the Results bridge draws for this measure (its `decisionAttribution`), in the order it reads them: the market first with what it did, then what earlier
 * rounds carried in, then "Your decisions" (the sum, each decision that moved it listed under it, biggest first), then "Your card answers", then how they interact. The fixed lines
 * stay even at zero (a player looks for their own decisions); a report without that attribution falls back to the per-cause rows, where rows that round to nothing are left out.
 */
export function whyLines(kpi: KpiId, report: RoundReport, catalog: ContentCatalog): WhyRow[] {
  const d = decompose(report, catalog, kpi as HeadlineId, "twins");
  if (d === null) return whyRows(kpi, report.attribution, catalog);
  const note = marketDriversNote(report.attribution.marketDrivers, kpi as HeadlineId);
  const text = (delta: number): string => formatKpiChange(kpi, delta, { signed: true });
  const children = d.bars.filter((b) => b.kind === "decision" && /[1-9]/.test(text(b.delta))).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const one = (kind: "market" | "carried" | "cardAnswers" | "interaction"): WhyRow[] => d.bars.filter((b) => b.kind === kind).map((b) => ({ label: b.label, text: text(b.delta), delta: b.delta, ...(b.kind === "market" && note !== null ? { note } : b.reason === undefined ? {} : { note: b.reason }) }));
  const decisions: WhyRow = { label: DECISIONS_LABEL, text: text(d.decisionsTotal), delta: d.decisionsTotal, ...(children.length === 0 ? {} : { note: children.map((b) => `${b.label} ${text(b.delta)}`).join("; ") }) };
  return [...one("market"), ...one("carried"), decisions, ...one("cardAnswers"), ...one("interaction")];
}

/** Every measure the attribution explains, as the Coach's offline answers: question about it, answer from the attribution. */
export function whyExplanations(attribution: Attribution, catalog: ContentCatalog, fyLabel: string): Readonly<Partial<Record<KpiId, string>>> {
  return Object.fromEntries(WHY_KPIS.flatMap((kpi) => { const a = whyAnswer(kpi, attribution, catalog, fyLabel); return a === null ? [] : [[kpi, a] as const]; }));
}

/** The measures that carry a "Why?" (the engine's per-cause KPI moves cover them). */
export const WHY_KPIS = ["nim", "rotce", "cet1Reported"] as const satisfies readonly KpiId[];

/** One "Why?" is open at a time: opening one tells the others (by the id of the one that opened) to close. */
const WHY_OPENED = "gx-why-opened";

/** `total`: the measure's whole move from the previous year-end to this one, so the rows add up on the page (what the named causes leave is "Everything else"). */
export function GxWhy({ kpi, report, catalog, fyLabel, total, span }: { readonly kpi: KpiId; readonly report: RoundReport; readonly catalog: ContentCatalog; readonly fyLabel: string; readonly total?: number; /** What `total` covers, from `changeSpanLabel`; absent reads "over <fyLabel>". */ readonly span?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const closeIfOther = (e: Event) => { if ((e as CustomEvent<string>).detail !== id) setOpen(false); };
    document.addEventListener(WHY_OPENED, closeIfOther);
    return () => document.removeEventListener(WHY_OPENED, closeIfOther);
  }, [id]);
  const named = whyLines(kpi, report, catalog);
  const rest = total === undefined ? 0 : total - named.reduce((t, r) => t + r.delta, 0);
  const restText = formatKpiChange(kpi, rest, { signed: true });
  // The rows must sum to the headline: what the named causes leave is said, not dropped.
  const rows: WhyRow[] = total !== undefined && /[1-9]/.test(restText) ? [...named, { label: "Everything else", text: restText, delta: rest }] : named;
  const coach = useCoach();
  const asSheet = useSheetMode();
  const answer = answerOf(kpi, rows, fyLabel);
  return (
    <span ref={anchor}>
      <button type="button" className="gx-why-btn" aria-expanded={open} aria-controls={id} data-testid={`cockpit.why.${kpi}`} aria-label={`Why did ${kpiLabel(kpi)} move?`} title="Why did this move?"
        onClick={() => { if (!open) document.dispatchEvent(new CustomEvent(WHY_OPENED, { detail: id })); setOpen((o) => !o); }}>?</button>
      {open ? (
        <GxPopover anchor={anchor} id={id} label={`Why did ${kpiLabel(kpi)} move`} onClose={() => setOpen(false)}>
          {/* the sheet's title already asks the question: it says only how far the measure moved */}
          {asSheet ? (total === undefined ? null : <b>Moved {formatKpiChange(kpi, total, { signed: true })} {span ?? `over ${fyLabel}`}</b>)
            : <b>Why did {kpiLabel(kpi)} move{total === undefined ? "" : ` (${formatKpiChange(kpi, total, { signed: true })} ${span ?? `over ${fyLabel}`})`}?</b>}
          <span className="gx-num" style={{ display: "block", marginTop: 8 }}>
            {rows.length === 0 ? <span className="gx-muted">It barely moved this year.</span> : rows.map((r) => <span key={r.label} className="gx-why-row"><span className="gx-muted">{r.label}{r.note === undefined ? null : <small style={{ display: "block" }}>{r.note}</small>}</span><b>{r.text}</b></span>)}
          </span>
          <span className="gx-why" style={{ color: "var(--ink-3)" }}>The same breakdown as your {fyLabel} results. Deterministic, no AI.</span>
          <button type="button" className="gx-btn gx-btn--secondary" style={{ marginTop: 10 }} data-testid={`cockpit.why.${kpi}.explain`}
            onClick={() => { setOpen(false); coach.open({ screen: "cockpit", label: `Cockpit · ${kpiLabel(kpi)}`, ask: `Why did our ${kpiLabel(kpi)} move?`, answer: answer ?? "", explain: whyExplanations(report.attribution, catalog, fyLabel) }); }}>Explain this number</button>
        </GxPopover>
      ) : null}
    </span>
  );
}
